<template>
  <div class="admin-main">

    <div class="header">
      <h2>선물 수령 내역</h2>
    </div>

    <div class="content">

      <!-- 탭 -->
      <div class="tab-bar">
        <button 
          :class="{ active: activeTab === 'all' }" 
          @click="activeTab = 'all'; fetchAll()"
        >전체</button>
        <button 
          :class="{ active: activeTab === 'gift' }" 
          @click="activeTab = 'gift'; fetchGiftHistory()"
        >기프티콘 선물</button>
        <button 
          :class="{ active: activeTab === 'roulette' }" 
          @click="activeTab = 'roulette'; fetchRouletteHistory()"
        >룰렛 당첨</button>
      </div>

      <!-- 요약 카드 -->
      <div class="item">
        <ul class="row-list">
          <li>
            <div class="top">
              <div class="left">
                <h2>전체 발송</h2>
                <span>기프티콘 + 룰렛</span>
              </div>
              <div class="right">
                <h2>{{ stats.total }}</h2>
              </div>
            </div>
            <div class="bottom">
              <span>총 {{ stats.total }}건 발송</span>
            </div>
          </li>
          <li>
            <div class="top">
              <div class="left">
                <h2>수령 완료</h2>
                <span>수령/발송 성공</span>
              </div>
              <div class="right">
                <h2 class="claimed-count">{{ stats.claimed }}</h2>
              </div>
            </div>
            <div class="bottom">
              <span>{{ stats.total > 0 ? Math.round(stats.claimed / stats.total * 100) : 0 }}% 수령률</span>
            </div>
          </li>
          <li>
            <div class="top">
              <div class="left">
                <h2>대기 중</h2>
                <span>미수령 / 발송대기</span>
              </div>
              <div class="right">
                <h2 class="pending-count">{{ stats.pending }}</h2>
              </div>
            </div>
            <div class="bottom">
              <span>확인이 필요합니다</span>
            </div>
          </li>
          <li>
            <div class="top">
              <div class="left">
                <h2>룰렛 당첨</h2>
                <span>당첨 건수</span>
              </div>
              <div class="right">
                <h2>{{ stats.rouletteWin }}</h2>
              </div>
            </div>
            <div class="bottom">
              <span>꽝 제외 당첨만</span>
            </div>
          </li>
        </ul>
      </div>

      <!-- 검색 & 필터 -->
      <div class="filter-bar">
        <input 
          type="text" 
          v-model="searchKeyword" 
          placeholder="이름, 전화번호, 상품명 검색..."
          @input="handleSearch"
        />
        <select v-model="statusFilter" @change="handleSearch">
          <option value="">전체 상태</option>
          <option value="claimed">수령 완료</option>
          <option value="sent">발송 완료</option>
          <option value="pending">대기 중</option>
        </select>
        <select v-model="dateFilter" @change="handleSearch">
          <option value="">전체 기간</option>
          <option value="today">오늘</option>
          <option value="week">최근 7일</option>
          <option value="month">최근 30일</option>
        </select>
      </div>

      <!-- 내역 리스트 -->
      <div class="whole">
        <div class="section-header">
          <h3>{{ activeTab === 'gift' ? '기프티콘 선물 내역' : activeTab === 'roulette' ? '룰렛 당첨 내역' : '전체 수령 내역' }}</h3>
          <span class="total-count">{{ filteredList.length }}건</span>
        </div>

        <!-- 테이블 헤더 -->
        <div class="table-header" v-if="filteredList.length > 0">
          <span class="col-status">상태</span>
          <span class="col-type">유형</span>
          <span class="col-product">상품</span>
          <span class="col-sender">보낸 사람</span>
          <span class="col-receiver">받는 사람</span>
          <span class="col-phone">전화번호</span>
          <span class="col-date">일시</span>
        </div>

        <!-- 데이터 목록 -->
        <div class="recent-list" v-if="filteredList.length > 0">
          <div 
            class="recent-item" 
            v-for="item in paginatedList" 
            :key="item.id"
          >
            <span class="col-status">
              <span class="status-badge" :class="item.statusClass">{{ item.statusLabel }}</span>
            </span>
            <span class="col-type">
              <span class="type-badge" :class="item.type">{{ item.type === 'gift' ? '선물' : '룰렛' }}</span>
            </span>
            <span class="col-product">
              <span class="product-name">{{ item.productName }}</span>
              <span class="product-desc" v-if="item.productDesc">{{ item.productDesc }}</span>
            </span>
            <span class="col-sender">{{ item.senderName || '-' }}</span>
            <span class="col-receiver">{{ item.receiverName || '-' }}</span>
            <span class="col-phone">{{ formatPhone(item.phone) }}</span>
            <span class="col-date">{{ formatDate(item.date) }}</span>
          </div>
        </div>

        <!-- 데이터 없음 -->
        <div class="no-data" v-else>
          <p>{{ loading ? '불러오는 중...' : '수령 내역이 없습니다.' }}</p>
        </div>

        <!-- 페이지네이션 -->
        <div class="pagination" v-if="totalPages > 1">
          <button :disabled="currentPage <= 1" @click="currentPage--">&lt;</button>
          <template v-for="p in visiblePages" :key="p">
            <button 
              v-if="p !== '...'" 
              :class="{ active: currentPage === p }" 
              @click="currentPage = p"
            >{{ p }}</button>
            <span v-else class="dots">...</span>
          </template>
          <button :disabled="currentPage >= totalPages" @click="currentPage++">&gt;</button>
        </div>
      </div>

    </div>
  </div>
</template>


<script>
import { inject, ref, computed, onMounted } from "vue";

export default {
  name: "adminGiftHistory",
  setup() {
    const sql_query = inject("sql_query");

    const activeTab = ref("all");
    const loading = ref(false);
    const searchKeyword = ref("");
    const statusFilter = ref("");
    const dateFilter = ref("");
    const currentPage = ref(1);
    const pageSize = 20;

    // 원본 데이터
    const giftList = ref([]);
    const rouletteList = ref([]);

    // 통계
    const stats = ref({ total: 0, claimed: 0, pending: 0, rouletteWin: 0 });

    // ========== 데이터 조회 ==========

    async function fetchAll() {
      loading.value = true;
      await Promise.all([fetchGiftHistory(), fetchRouletteHistory()]);
      loading.value = false;
      updateStats();
    }

    async function fetchGiftHistory() {
      try {
        const res = await sql_query(`
          SELECT 
            gl.idx, gl.token, gl.sender_id, gl.receiver_name, gl.receiver_phone,
            gl.goods_code, gl.goods_name, gl.brand_name, gl.goods_img,
            gl.gift_message, gl.status, gl.claimed_at, gl.created_at, gl.expires_at,
            m.name as sender_name
          FROM gift_links gl
          LEFT JOIN member m ON gl.sender_id = m.mb_id
          ORDER BY gl.created_at DESC
        `);

        if (res && !res.error) {
          giftList.value = (res || []).map(row => ({
            id: 'gift_' + row.idx,
            type: 'gift',
            productName: row.goods_name || '기프티콘',
            productDesc: row.brand_name || '',
            senderName: row.sender_name || row.sender_id || '',
            receiverName: row.receiver_name || '',
            phone: row.receiver_phone || '',
            status: row.status || 'pending',
            statusClass: row.status === 'claimed' ? 'claimed' : row.status === 'expired' ? 'expired' : 'pending',
            statusLabel: row.status === 'claimed' ? '수령완료' : row.status === 'expired' ? '만료' : '대기중',
            date: row.claimed_at || row.created_at,
            createdAt: row.created_at
          }));
        }
      } catch (e) {
        console.error('기프티콘 내역 조회 실패:', e);
      }
    }

    async function fetchRouletteHistory() {
      try {
        const res = await sql_query(`
          SELECT 
            rh.idx, rh.mb_id, rh.ip_address, rh.prize_name, rh.prize_value,
            rh.is_winner, rh.prize_detail, rh.coupon_code, rh.coupon_status, rh.created_at,
            m.name as sender_name
          FROM roulette_history rh
          LEFT JOIN member m ON rh.mb_id = m.mb_id
          WHERE rh.is_winner = 1
          ORDER BY rh.created_at DESC
        `);

        if (res && !res.error) {
          rouletteList.value = (res || []).map(row => {
            let detail = {};
            try { detail = typeof row.prize_detail === 'string' ? JSON.parse(row.prize_detail) : (row.prize_detail || {}); } catch(e) { detail = {}; }
            
            const isAgent = row.ip_address === 'agent';
            return {
              id: 'roulette_' + row.idx,
              type: 'roulette',
              productName: row.prize_name || '경품',
              productDesc: detail.description || '',
              senderName: row.sender_name || row.mb_id || '',
              receiverName: isAgent ? (row.sender_name || '본인') : '',
              phone: isAgent ? '' : (row.ip_address || ''),
              status: row.coupon_status || 'pending',
              statusClass: row.coupon_status === 'sent' ? 'claimed' : 'pending',
              statusLabel: row.coupon_status === 'sent' ? '발송완료' : row.coupon_status === 'used' ? '사용완료' : '대기중',
              date: row.created_at,
              createdAt: row.created_at
            };
          });
        }
      } catch (e) {
        console.error('룰렛 내역 조회 실패:', e);
      }
    }

    function updateStats() {
      const allItems = [...giftList.value, ...rouletteList.value];
      stats.value = {
        total: allItems.length,
        claimed: allItems.filter(i => i.statusClass === 'claimed').length,
        pending: allItems.filter(i => i.statusClass === 'pending').length,
        rouletteWin: rouletteList.value.length
      };
    }

    // ========== 필터링 ==========

    const combinedList = computed(() => {
      if (activeTab.value === 'gift') return giftList.value;
      if (activeTab.value === 'roulette') return rouletteList.value;
      return [...giftList.value, ...rouletteList.value].sort((a, b) => new Date(b.date) - new Date(a.date));
    });

    const filteredList = computed(() => {
      let list = combinedList.value;

      // 키워드 검색
      if (searchKeyword.value.trim()) {
        const kw = searchKeyword.value.trim().toLowerCase();
        list = list.filter(item => 
          (item.productName || '').toLowerCase().includes(kw) ||
          (item.senderName || '').toLowerCase().includes(kw) ||
          (item.receiverName || '').toLowerCase().includes(kw) ||
          (item.phone || '').replace(/-/g, '').includes(kw.replace(/-/g, ''))
        );
      }

      // 상태 필터
      if (statusFilter.value) {
        list = list.filter(item => item.statusClass === statusFilter.value || item.status === statusFilter.value);
      }

      // 기간 필터
      if (dateFilter.value) {
        const now = new Date();
        let cutoff;
        if (dateFilter.value === 'today') {
          cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        } else if (dateFilter.value === 'week') {
          cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        } else if (dateFilter.value === 'month') {
          cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        }
        if (cutoff) {
          list = list.filter(item => new Date(item.date) >= cutoff);
        }
      }

      return list;
    });

    // ========== 페이지네이션 ==========

    const totalPages = computed(() => Math.ceil(filteredList.value.length / pageSize));

    const paginatedList = computed(() => {
      const start = (currentPage.value - 1) * pageSize;
      return filteredList.value.slice(start, start + pageSize);
    });

    const visiblePages = computed(() => {
      const total = totalPages.value;
      const cur = currentPage.value;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

      const pages = [];
      pages.push(1);
      if (cur > 3) pages.push('...');
      for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) {
        pages.push(i);
      }
      if (cur < total - 2) pages.push('...');
      pages.push(total);
      return pages;
    });

    function handleSearch() {
      currentPage.value = 1;
    }

    // ========== 포맷 ==========

    function formatDate(dateStr) {
      if (!dateStr) return "-";
      const d = new Date(dateStr);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const h = String(d.getHours()).padStart(2, "0");
      const min = String(d.getMinutes()).padStart(2, "0");
      return `${y}.${m}.${day} ${h}:${min}`;
    }

    function formatPhone(phone) {
      if (!phone) return "-";
      const nums = phone.replace(/[^0-9]/g, '');
      if (nums.length === 11) return `${nums.slice(0,3)}-${nums.slice(3,7)}-${nums.slice(7)}`;
      return phone;
    }

    // ========== 마운트 ==========

    onMounted(() => {
      fetchAll();
    });

    return {
      activeTab,
      loading,
      searchKeyword,
      statusFilter,
      dateFilter,
      currentPage,
      stats,
      filteredList,
      paginatedList,
      totalPages,
      visiblePages,
      handleSearch,
      formatDate,
      formatPhone,
      fetchAll,
      fetchGiftHistory,
      fetchRouletteHistory,
    };
  }
};
</script>


<style scoped>
.admin-main {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  background-color: var(--color-background);
}

.admin-main > .header {
  width: 100%;
  height: 60px;
  display: flex;
  justify-content: start;
  align-items: center;
  color: var(--color-text-primary);
  border-bottom: 1px solid var(--color-surface-reverse);
  padding: 16px;
  box-sizing: border-box;
}

.admin-main > .header > h2 {
  font-size: 18px;
  margin: 0;
  padding: 0;
}

.content {
  width: 100%;
  height: 100%;
  flex: 1;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  gap: 16px;
  overflow-y: auto;
}

/* ========== 탭 ========== */
.tab-bar {
  width: 100%;
  display: flex;
  gap: 8px;
}

.tab-bar > button {
  padding: 8px 20px;
  border: 1px solid var(--color-text-secondary);
  border-radius: 8px;
  background: transparent;
  color: var(--color-text-primary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.tab-bar > button.active {
  background: var(--color-surface-reverse);
  border-color: var(--color-text-primary);
  font-weight: 700;
}

/* ========== 요약 카드 ========== */
.item {
  width: 100%;
  height: fit-content;
  display: flex;
}

.item > ul.row-list {
  width: 100%;
  height: fit-content;
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: row;
  gap: 12px;
}

.item > ul.row-list > li {
  width: 100%;
  height: 120px;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  font-size: 16px;
  color: var(--color-text-primary);
  background-color: var(--color-surface-reverse);
  border: 1px solid var(--color-text-secondary);
  border-radius: 12px;
  margin: 0;
}

.item > ul.row-list > li > .top {
  width: 100%;
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px;
  box-sizing: border-box;
}

.item > ul.row-list > li > .top > .left {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  gap: 6px;
}

.item > ul.row-list > li > .top > .left > h2 {
  font-size: 20px;
  color: var(--color-text-primary);
  margin: 0;
  padding: 0;
}

.item > ul.row-list > li > .top > .left > span {
  font-size: 14px;
  color: var(--color-text-primary);
}

.item > ul.row-list > li > .top > .right {
  height: 50px;
  width: auto;
  aspect-ratio: 1/1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.item > ul.row-list > li > .top > .right > h2 {
  font-size: 24px;
  color: var(--color-text-primary);
  margin: 0;
  padding: 0;
}

.item > ul.row-list > li > .bottom {
  width: 100%;
  height: 36px;
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: center;
  border-top: 1px solid var(--color-text-primary);
  padding: 12px;
  box-sizing: border-box;
}

.item > ul.row-list > li > .bottom > span {
  font-size: 14px;
  color: var(--color-text-primary);
}

.claimed-count { color: #22c55e !important; }
.pending-count { color: #ffaa00 !important; }

/* ========== 필터 바 ========== */
.filter-bar {
  width: 100%;
  display: flex;
  gap: 10px;
  align-items: center;
}

.filter-bar > input {
  flex: 1;
  height: 40px;
  padding: 0 14px;
  border: 1px solid var(--color-text-secondary);
  border-radius: 8px;
  background: var(--color-surface-reverse);
  color: var(--color-text-primary);
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
}

.filter-bar > input:focus {
  border-color: var(--color-text-primary);
}

.filter-bar > input::placeholder {
  color: var(--color-text-secondary);
}

.filter-bar > select {
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--color-text-secondary);
  border-radius: 8px;
  background: var(--color-surface-reverse);
  color: var(--color-text-primary);
  font-size: 14px;
  cursor: pointer;
  outline: none;
}

/* ========== 내역 테이블 ========== */
.whole {
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  gap: 0;
  background-color: var(--color-surface-reverse);
  border-radius: 12px;
  overflow: hidden;
}

.section-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  box-sizing: border-box;
}

.section-header > h3 {
  font-size: 16px;
  color: var(--color-text-primary);
  margin: 0;
  font-weight: 600;
}

.total-count {
  font-size: 13px;
  color: var(--color-text-secondary);
  font-weight: 500;
}

/* 테이블 헤더 */
.table-header {
  width: 100%;
  display: flex;
  align-items: center;
  padding: 10px 16px;
  box-sizing: border-box;
  border-top: 1px solid var(--color-background-light);
  border-bottom: 1px solid var(--color-background-light);
  gap: 8px;
}

.table-header > span {
  font-size: 12px;
  font-weight: 700;
  color: var(--color-text-secondary);
  text-transform: uppercase;
}

/* 컬럼 너비 */
.col-status { width: 80px; flex-shrink: 0; }
.col-type { width: 60px; flex-shrink: 0; }
.col-product { flex: 2; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.col-sender { flex: 1; min-width: 0; }
.col-receiver { flex: 1; min-width: 0; }
.col-phone { width: 120px; flex-shrink: 0; }
.col-date { width: 130px; flex-shrink: 0; text-align: right; }

.recent-list {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.recent-item {
  width: 100%;
  display: flex;
  align-items: center;
  padding: 12px 16px;
  box-sizing: border-box;
  border-bottom: 1px solid var(--color-background-light);
  gap: 8px;
  transition: background 0.1s;
}

.recent-item:hover {
  background: var(--color-background);
}

.recent-item > span {
  font-size: 13px;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-desc {
  font-size: 11px;
  color: var(--color-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 상태 뱃지 */
.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.status-badge.claimed {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.status-badge.pending {
  background: rgba(255, 170, 0, 0.15);
  color: #ffaa00;
}

.status-badge.expired {
  background: rgba(156, 163, 175, 0.15);
  color: #9ca3af;
}

/* 유형 뱃지 */
.type-badge {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.type-badge.gift {
  background: rgba(102, 126, 234, 0.15);
  color: #667eea;
}

.type-badge.roulette {
  background: rgba(255, 152, 0, 0.15);
  color: #FF9800;
}

/* 페이지네이션 */
.pagination {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 16px;
  box-sizing: border-box;
}

.pagination > button {
  min-width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-text-secondary);
  border-radius: 8px;
  background: transparent;
  color: var(--color-text-primary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.pagination > button.active {
  background: var(--color-text-primary);
  color: var(--color-background);
  border-color: var(--color-text-primary);
  font-weight: 700;
}

.pagination > button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.pagination > .dots {
  font-size: 13px;
  color: var(--color-text-secondary);
  padding: 0 4px;
}

/* 데이터 없음 */
.no-data {
  width: 100%;
  flex: 1;
  min-height: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.no-data > p {
  font-size: 14px;
  color: var(--color-text-secondary);
}
</style>
